/**
 * Every admin path this app calls, in one place — and, for the four slice 8
 * routes, where each one comes from.
 *
 * WHY THIS MODULE EXISTS AT ALL. Slice 0's two calls hard-coded their paths at
 * the call site, which was right when there were two of them and both already
 * existed on the API. Slice 8 adds four, and three of them were being written by
 * a different pair of hands at the same time as this screen. A path string
 * duplicated across four api modules is four edits when the API lands on a
 * different spelling, and the fourth is the one that gets missed — it stays
 * green under `tsc`, because a wrong URL is a perfectly well-typed string, and
 * surfaces as a 404 on the one panel nobody clicked during review.
 *
 * That is not a hypothetical: it is what happened. {@link tenantBalancesPath}
 * shipped pointing at a route `AdminCreditsController` did not have, so the
 * credits panel 404'd on **every** load, and the "the balance could not be read"
 * copy written for a rare outage was the only state the panel could reach. It is
 * fixed on the API side — the controller now routes `GET balances` — and the
 * episode is left written down here because the lesson is about this file: a
 * path named in one place is still only a claim about another repo directory
 * until somebody checks it against the controller.
 *
 * WHERE EACH PATH COMES FROM — all four read off the API's own controllers,
 * checked rather than assumed:
 *
 *   - `PATCH /admin/tenants/:tenantId` — `TenantsController.update`.
 *   - `POST /admin/tenants/:tenantId/credits/topup` — `AdminCreditsController`,
 *     whose class path is `admin/tenants/:tenantId/credits`. It answers `201`
 *     when it appended a ledger entry and `200` when it replayed an idempotency
 *     key and moved nothing; this client reads that distinction off the body's
 *     `applied` rather than off the status, because the body is the contract.
 *     A token reused for a *different* channel or amount is neither: it is a
 *     `409`, and reaches this app as an ordinary API error.
 *   - `GET /admin/tenants/:tenantId/credits/balances` — the same controller's
 *     `balances`, mirroring the tenant-facing `GET /credits/balances` exactly.
 *   - `GET /admin/tenants/:tenantId/audit` — `TenantsController.auditTrail`. It
 *     takes pagination **only**: the API narrows `adminAuditListQuerySchema` by
 *     omitting `tenantId`, because the tenant is already in the path and a
 *     filter that can disagree with the path is a filter that will. It reads
 *     `admin_audit`, which holds tenant *detail* edits and no credit grants —
 *     `TenantAuditPanel` is written to say so rather than imply otherwise.
 *
 * IF THE API MOVES A ROUTE, this is the file to change, and it is a one-line
 * edit per route. Nothing below is a fallback or a probe: this app does not try
 * a second URL when the first 404s, because a client that guesses its way to an
 * endpoint cannot tell "the route moved" from "the route is not deployed", and
 * both of those are things a person needs to be told plainly.
 */

/** `admin/tenants` — the collection. Create, list. */
export const TENANTS_PATH = '/admin/tenants';

/** One tenant: read (slice 0) and correct (slice 8). */
export function tenantPath(tenantId: string): string {
  return `${TENANTS_PATH}/${encodeURIComponent(tenantId)}`;
}

/**
 * That tenant's credit balances, one row per channel.
 *
 * `AdminCreditsController.balances`. `/credits/balances` rather than `/credits`,
 * mirroring the tenant-facing route exactly, so the two surfaces cannot drift
 * into two spellings of the same read — and so the response shape can stay the
 * one `creditBalancesResponseSchema` describes for both.
 */
export function tenantBalancesPath(tenantId: string): string {
  return `${tenantPath(tenantId)}/credits/balances`;
}

/**
 * Grant credits to that tenant. `POST`, because it appends a ledger entry.
 *
 * Named `topup` and not `credits` for the reason `contracts/admin.ts` gives for
 * keeping the operator's word and the ledger's word apart: the person is doing a
 * top-up, the row it writes is a `grant`, and the audit action is
 * `tenant.credits_granted`. The route is named for what the person is doing.
 */
export function tenantTopupPath(tenantId: string): string {
  return `${tenantPath(tenantId)}/credits/topup`;
}

/**
 * What staff have changed about that workspace, newest first.
 *
 * Nested under the tenant rather than a cross-tenant collection filtered by id,
 * which is how the API routes it. The practical consequence for this client is
 * that the tenant must **not** also travel as a query parameter: the API omits
 * `tenantId` from the query schema precisely so the path and the filter cannot
 * name two different workspaces.
 */
export function tenantAuditPath(tenantId: string): string {
  return `${tenantPath(tenantId)}/audit`;
}

/**
 * How many flows that workspace has in each status, and how many rule
 * segments — slice 71. `TenantsController.usage`. Counts only: the route is
 * named `usage` rather than after either table because it reads no row of
 * either (`tenant-usage.service.ts` in the API argues the boundary).
 */
export function tenantUsagePath(tenantId: string): string {
  return `${tenantPath(tenantId)}/usage`;
}

/** `admin/interests` — the sales desk (migration 0040). List and create. */
export const INTERESTS_PATH = '/admin/interests';

/** One interest and its timeline. */
export function interestPath(interestId: string): string {
  return `${INTERESTS_PATH}/${encodeURIComponent(interestId)}`;
}

/** New / in progress / closed. A sub-resource because it writes a timeline row. */
export function interestStatusPath(interestId: string): string {
  return `${interestPath(interestId)}/status`;
}

/** A timestamped note on the timeline. Append-only. */
export function interestNotesPath(interestId: string): string {
  return `${interestPath(interestId)}/notes`;
}
